import { useMemo } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "../../utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select";
import { buildCaptionOptions, formatMonthCaption, type LocaleTag } from "../format";
import { PART_CLASSES } from "../dateTimePickerStyles";
import type { DateTimePickerClassNames } from "../types";

// Calendar header: month/year selects (or a label) with the nav arrows overlaid.

interface MonthCaptionProps {
  month: Date;
  locale?: LocaleTag;
  showDropdowns: boolean;
  navStart: Date;
  navEnd: Date;
  canGoPrev: boolean;
  canGoNext: boolean;
  canGoPrevYear?: boolean;
  canGoNextYear?: boolean;
  showPrev: boolean;
  showNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onPrevYear?: () => void;
  onNextYear?: () => void;
  onMonthChange: (month: Date) => void;
  classNames?: DateTimePickerClassNames;
  prevLabel?: string;
  nextLabel?: string;
  prevYearLabel?: string;
  nextYearLabel?: string;
}

export function MonthCaption({
  month,
  locale,
  showDropdowns,
  navStart,
  navEnd,
  canGoPrev,
  canGoNext,
  canGoPrevYear = canGoPrev,
  canGoNextYear = canGoNext,
  showPrev,
  showNext,
  onPrev,
  onNext,
  onPrevYear,
  onNextYear,
  onMonthChange,
  classNames,
  prevLabel = "Previous month",
  nextLabel = "Next month",
  prevYearLabel = "Previous year",
  nextYearLabel = "Next year",
}: MonthCaptionProps) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();

  const { years, months } = useMemo(
    () => buildCaptionOptions({ locale, year, monthIndex, navStart, navEnd }),
    [locale, year, monthIndex, navStart, navEnd],
  );

  const showYearNav = true;

  return (
    <div
      data-part="caption"
      className={cn(PART_CLASSES.caption, classNames?.caption)}
    >
      {showPrev ? (
        <div data-part="nav-prev" className="flex items-center gap-1 shrink-0">
          {showYearNav && onPrevYear && (
            <button
              type="button"
              aria-label={prevYearLabel}
              title={prevYearLabel}
              disabled={!canGoPrevYear}
              onClick={onPrevYear}
              data-part="nav-button-prev-year"
              className={cn(PART_CLASSES.navButton, classNames?.navButton)}
            >
              <ChevronsLeft className="size-3.5" aria-hidden />
            </button>
          )}
          <button
            type="button"
            aria-label={prevLabel}
            title={prevLabel}
            disabled={!canGoPrev}
            onClick={onPrev}
            data-part="nav-button-prev-month"
            className={cn(PART_CLASSES.navButton, classNames?.navButton)}
          >
            <ChevronLeft className="size-3.5" aria-hidden />
          </button>
        </div>
      ) : (
        <div className={cn(showYearNav ? "w-16" : "w-8", "shrink-0")} />
      )}

      {showDropdowns ? (
        <div className="flex items-center justify-center gap-2 min-w-0 flex-1 px-1">
          <Select
            value={String(monthIndex)}
            onValueChange={(next) => onMonthChange(new Date(year, Number(next), 1))}
          >
            <SelectTrigger
              size="sm"
              aria-label="Month"
              className="h-7 w-[110px] min-w-[110px] px-2.5 text-xs gap-1 rounded-md font-semibold bg-muted/40 border-border/70 hover:bg-muted/70 transition-colors"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              portal={false}
              className="max-h-[220px] min-w-[var(--radix-select-trigger-width)] w-[var(--radix-select-trigger-width)]"
            >
              {months.map((entry) => (
                <SelectItem
                  key={entry.index}
                  value={String(entry.index)}
                  className="text-xs py-1 pl-2.5 pr-6 cursor-pointer"
                >
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={String(year)}
            onValueChange={(next) => onMonthChange(new Date(Number(next), monthIndex, 1))}
          >
            <SelectTrigger
              size="sm"
              aria-label="Year"
              className="h-7 w-[76px] min-w-[76px] px-2.5 text-xs gap-1 rounded-md font-semibold bg-muted/40 border-border/70 hover:bg-muted/70 transition-colors"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              portal={false}
              className="max-h-[220px] min-w-[var(--radix-select-trigger-width)] w-[var(--radix-select-trigger-width)]"
            >
              {years.map((entry) => (
                <SelectItem
                  key={entry}
                  value={String(entry)}
                  className="text-xs py-1 pl-2.5 pr-6 cursor-pointer"
                >
                  {entry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <span
          data-part="caption-label"
          className={cn(PART_CLASSES.captionLabel, classNames?.captionLabel, "truncate text-center flex-1")}
        >
          {formatMonthCaption(month, locale)}
        </span>
      )}

      {showNext ? (
        <div data-part="nav-next" className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            aria-label={nextLabel}
            title={nextLabel}
            disabled={!canGoNext}
            onClick={onNext}
            data-part="nav-button-next-month"
            className={cn(PART_CLASSES.navButton, classNames?.navButton)}
          >
            <ChevronRight className="size-3.5" aria-hidden />
          </button>
          {showYearNav && onNextYear && (
            <button
              type="button"
              aria-label={nextYearLabel}
              title={nextYearLabel}
              disabled={!canGoNextYear}
              onClick={onNextYear}
              data-part="nav-button-next-year"
              className={cn(PART_CLASSES.navButton, classNames?.navButton)}
            >
              <ChevronsRight className="size-3.5" aria-hidden />
            </button>
          )}
        </div>
      ) : (
        <div className={cn(showYearNav ? "w-16" : "w-8", "shrink-0")} />
      )}
    </div>
  );
}
